import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  /** Sources are optional when Liquid outputs none; `videoElement` is always required. */
  requiredRefs = ['videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoElement } = this.refs;
    const videoSources = Array.isArray(this.refs.videoSources) ? this.refs.videoSources : [];

    for (const source of videoSources) {
      const url = source.dataset?.videoSource ?? source.getAttribute('data-video-source');

      if (url) source.setAttribute('src', url);
    }

    videoElement.load();

    // Dynamic <source> + load() does not reliably start playback on current Chrome; muted autoplay still needs play().
    const tryPlay = () => {
      void videoElement.play().catch(() => {});
    };

    tryPlay();
    videoElement.addEventListener('canplay', tryPlay, { once: true });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
